import { useCallback, useEffect, useState } from "react";
import { Apple, Check, ClipboardCopy, Globe, Loader2, RefreshCw, Rocket, Smartphone, Trash2, TriangleAlert } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { AndroidPackagePanel } from "@/components/AndroidPackagePanel";
import { ApnsPanel } from "@/components/ApnsPanel";
import { AppRequestQueue } from "@/components/AppRequestQueue";
import { PlayChecklist } from "@/components/PlayChecklist";
import { ReviewerAccess } from "@/components/ReviewerAccess";
import { ScreenshotStudio } from "@/components/ScreenshotStudio";
import { StoreArtPanel } from "@/components/StoreArtPanel";
import { TesterPanel } from "@/components/TesterPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const STATUSES = [["draft", "Draft"], ["building", "Building"], ["in_review", "In review"], ["live", "Live"]];

const KINDS = [
  ["office", "LimoLinkX Dispatch — office & admin"],
  ["chauffeur", "LimoLinkX Chauffeur"],
  ["passenger", "LimoLinkX Rides — passengers"],
  ["rider", "White-label passenger app (one operator)"],
];

const blank = { kind: "office", platform: "android", company_id: "", app_id: "", app_name: "",
                fingerprint: "", store_url: "", status: "draft" };

const Field = ({ label, hint, children }) => (
  <label className="block space-y-1">
    <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</span>
    {children}
    {hint && <span className="block text-[11px] text-slate-500">{hint}</span>}
  </label>
);

const copy = (text) => navigator.clipboard?.writeText(text);

export const StoreApps = () => {
  const [data, setData] = useState(null);
  const [links, setLinks] = useState(null);
  const [form, setForm] = useState(blank);
  const [config, setConfig] = useState(null);
  const [publish, setPublish] = useState(null);
  const [domain, setDomain] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [apps, assetlinks, host] = await Promise.all([
        api.get("/admin/mobile-apps"), api.get("/admin/mobile-apps/assetlinks"),
        api.get("/admin/mobile-apps/domain")]);
      setData(apps.data);
      setLinks(assetlinks.data);
      setDomain(host.data);
    } catch (e) {
      setError(errMsg(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const run = async (fn, message = "") => {
    setBusy(true); setError(""); setNotice("");
    try {
      await fn();
      if (message) setNotice(message);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const save = () => run(async () => {
    const { fingerprint, ...rest } = form;
    await api.post("/admin/mobile-apps", { ...rest, fingerprints: fingerprint ? [fingerprint] : [] });
    setForm(blank);
    await load();
  }, `${form.app_id} saved. Open "Publish" on its row for the store run-through.`);

  const remove = (app) => run(async () => {
    await api.delete(`/admin/mobile-apps/${app.id}`);
    if (config?.app?.id === app.id) setConfig(null);
    if (publish?.app?.id === app.id) setPublish(null);
    await load();
  }, `${app.app_id} removed.`);

  const open = (app, mode) => run(async () => {
    const { data: cfg } = await api.get(`/admin/mobile-apps/${app.id}/build-config`);
    if (mode === "publish") { setPublish(cfg); setConfig(null); } else { setConfig(cfg); setPublish(null); }
  });

  const tick = (step, done) => run(async () => {
    const { data: result } = await api.post(`/admin/mobile-apps/${publish.app.id}/checklist`,
      { step, done });
    setPublish({ ...publish, checklist: result.checklist });
  });

  const saveDomain = () => run(async () => {
    await api.put("/admin/mobile-apps/domain", { domain: domain.domain });
    await load();
  }, "Live domain saved — every value below now points at it.");

  const recheck = () => run(async () => {
    const { data: fresh } = await api.get("/admin/mobile-apps/assetlinks");
    setLinks(fresh);
    setNotice(fresh.in_sync ? "Your domain vouches for every registered app — installs will be clean."
                            : "The live file still does not match. Run the sync, then redeploy.");
  });

  const prefill = (request) => {
    setForm({ ...blank, kind: "rider",
              platform: request.stores.includes("android") ? "android" : "ios",
              company_id: request.company_id, app_id: request.suggested_app_id });
    setNotice(`Filled in for ${request.company_name}. Check the package id, then save.`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (!data) return <Loader2 data-testid="store-apps-loading" className="h-5 w-5 animate-spin text-gold" />;

  const operator = data.operators.find((o) => o.id === form.company_id);

  return (
    <div data-testid="store-apps" className="space-y-6">
      {error && <p data-testid="store-apps-error" className="text-sm text-red-400">{error}</p>}
      {notice && <p data-testid="store-apps-notice" className="text-sm text-emerald-400">{notice}</p>}

      <AppRequestQueue onRegister={prefill} onNotice={setNotice} />

      {domain && (
        <div data-testid="store-domain" className="rounded-xl border border-ink-600 bg-ink-800 p-5">
          <p className="flex items-center gap-2 font-display text-lg text-slate-100">
            <Globe className="h-4 w-4 text-gold" /> Your live domain
          </p>
          <p className="mt-1 text-sm text-slate-400">
            Every store build and both verification files point here — not at this preview. Set it
            once and every value on this page is the real one.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Input data-testid="store-domain-input" value={domain.domain}
              placeholder={domain.preview} className="max-w-sm"
              onChange={(e) => setDomain({ ...domain, domain: e.target.value })} />
            <Button data-testid="store-domain-save" size="sm" disabled={busy} onClick={saveDomain}>
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save
            </Button>
          </div>
          <p data-testid="store-domain-effective" className="mt-2 font-mono text-[11px] text-slate-500">
            Using {domain.effective}
          </p>
        </div>
      )}

      <div className="rounded-xl border border-ink-600 bg-ink-800 p-5">
        <p className="font-display text-lg text-slate-100">Register a store app</p>
        <p className="mt-1 text-sm text-slate-400">
          Three LimoLinkX apps that work for every operator — Dispatch, Chauffeur and Rides — plus a
          white-label passenger app for any operator on Business or above. The package id can never
          be changed once a build is on the store.
        </p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="App">
            <select data-testid="store-app-kind" value={form.kind}
              onChange={(e) => setForm({ ...form, kind: e.target.value, company_id: "" })}
              className="w-full rounded-md border border-ink-600 bg-ink-900 px-3 py-2 text-sm text-slate-200">
              {KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </Field>
          <Field label="Store">
            <select data-testid="store-app-platform" value={form.platform}
              onChange={(e) => setForm({ ...form, platform: e.target.value })}
              className="w-full rounded-md border border-ink-600 bg-ink-900 px-3 py-2 text-sm text-slate-200">
              <option value="android">Google Play</option>
              <option value="ios">Apple App Store</option>
            </select>
          </Field>
          {form.kind === "rider" && (
            <Field label="Operator" hint={operator && !operator.white_label
              ? `${operator.name} is on ${operator.plan} — a white-label app needs Business or above`
              : ""}>
              <select data-testid="store-app-operator" value={form.company_id}
                onChange={(e) => setForm({ ...form, company_id: e.target.value })}
                className="w-full rounded-md border border-ink-600 bg-ink-900 px-3 py-2 text-sm text-slate-200">
                <option value="">Choose an operator…</option>
                {data.operators.map((o) => (
                  <option key={o.id} value={o.id}>{`${o.name} — ${o.plan}`}</option>
                ))}
              </select>
            </Field>
          )}
          <Field label={form.platform === "ios" ? "Bundle id" : "Package name"}
            hint="Reverse domain, lower case — e.g. com.limolinkx.dispatch">
            <Input data-testid="store-app-id" value={form.app_id} placeholder="com.limolinkx.dispatch"
              onChange={(e) => setForm({ ...form, app_id: e.target.value })} />
          </Field>
          <Field label="Store name" hint="Leave blank to use the standard name for this app">
            <Input data-testid="store-app-name" value={form.app_name} placeholder="LimoLinkX Dispatch"
              onChange={(e) => setForm({ ...form, app_name: e.target.value })} />
          </Field>
          {form.platform === "android" && (
            <Field label="SHA-256 fingerprints"
              hint="Play Console → Test and release → App integrity. Paste the whole block if you like — we pick the SHA-256 lines out, and the upload key can go in too.">
              <textarea data-testid="store-app-fingerprint" rows={3} value={form.fingerprint}
                onChange={(e) => setForm({ ...form, fingerprint: e.target.value })}
                placeholder="SHA-256 certificate fingerprint: AB:CD:…:EF"
                className="w-full rounded-md border border-ink-600 bg-ink-900 px-3 py-2 font-mono text-[11px] text-slate-200" />
            </Field>
          )}
          <Field label="Store listing URL" hint="Fill this in once the listing is public">
            <Input data-testid="store-app-url" value={form.store_url} placeholder="https://play.google.com/…"
              onChange={(e) => setForm({ ...form, store_url: e.target.value })} />
          </Field>
          <Field label="Where it is">
            <select data-testid="store-app-status" value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
              className="w-full rounded-md border border-ink-600 bg-ink-900 px-3 py-2 text-sm text-slate-200">
              {STATUSES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </Field>
        </div>
        <Button data-testid="store-app-save" disabled={busy || !form.app_id} onClick={save} className="mt-4">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save store app
        </Button>
      </div>

      <div data-testid="store-apps-list" className="space-y-3">
        {!data.apps.length && (
          <p data-testid="store-apps-empty" className="text-sm text-slate-500">
            No store apps registered yet.
          </p>
        )}
        {data.apps.map((app) => (
          <div key={app.id} data-testid={`store-app-${app.app_id}`}
            className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-ink-600 bg-ink-800 p-4">
            <div className="min-w-0">
              <p className="flex items-center gap-2 font-display text-slate-100">
                {app.platform === "ios" ? <Apple className="h-4 w-4 text-slate-400" />
                  : <Smartphone className="h-4 w-4 text-slate-400" />}
                {app.app_name}
                <span className="rounded-full border border-ink-600 px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest text-slate-400">
                  {STATUSES.find(([k]) => k === app.status)?.[1]}
                </span>
                {Boolean(Object.keys(app.checklist || {}).length) && (
                  <span data-testid={`store-app-progress-${app.app_id}`}
                    className="font-mono text-[10px] uppercase tracking-widest text-emerald-400">
                    {Object.keys(app.checklist).length} steps done
                  </span>
                )}
              </p>
              <p className="mt-1 font-mono text-[11px] text-slate-500">
                {app.app_id} → {app.home}
                {app.platform === "android" && !app.fingerprints.length && (
                  <span data-testid={`store-app-nofingerprint-${app.app_id}`} className="ml-2 text-amber-400">
                    no fingerprint yet — the app will open with a browser bar
                  </span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button data-testid={`store-app-publish-${app.app_id}`} size="sm" disabled={busy}
                onClick={() => open(app, "publish")} className="bg-gold text-ink-900 hover:bg-gold-light">
                <Rocket className="mr-2 h-4 w-4" /> Publish
              </Button>
              <Button data-testid={`store-app-config-${app.app_id}`} variant="outline" size="sm"
                onClick={() => open(app, "config")}>Build config</Button>
              <Button data-testid={`store-app-delete-${app.app_id}`} variant="ghost" size="sm"
                disabled={busy} onClick={() => remove(app)} className="text-slate-400 hover:text-red-400">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {publish && (
        <>
          <ReviewerAccess app={publish.app}
            onCopy={(value, label) => { copy(value); setNotice(`${label} copied.`); }} />
          <PlayChecklist app={publish.app} steps={publish.steps} checklist={publish.checklist}
            busy={busy} onTick={tick}
            onCopy={(value, label) => { copy(value); setNotice(`${label} copied.`); }} />
          {publish.app.platform === "android" && (
            <AndroidPackagePanel app={publish.app} onNotice={setNotice} onBuilt={load} />
          )}
          {publish.app.platform === "android" && (
            <TesterPanel app={publish.app} onNotice={setNotice} />
          )}
          <ScreenshotStudio app={publish.app} onNotice={setNotice} />
          <StoreArtPanel app={publish.app} onNotice={setNotice} />
        </>
      )}

      {links && (
        <div data-testid="store-assetlinks" className="rounded-xl border border-ink-600 bg-ink-800 p-5">
          <p className="flex items-center gap-2 font-display text-lg text-slate-100">
            {links.in_sync ? <Check className="h-4 w-4 text-emerald-400" />
              : <TriangleAlert className="h-4 w-4 text-amber-400" />}
            Android verification file
          </p>
          <p className="mt-1 text-sm text-slate-400">
            Android checks <span className="font-mono text-[11px]">{links.url}</span> to confirm each
            app owns this domain. Without a match the app opens with a browser address bar across the
            top. The file ships inside the web build, so it goes live on the next deploy.
          </p>
          <p data-testid="store-assetlinks-state"
            className={`mt-3 text-sm ${links.in_sync ? "text-emerald-400" : "text-amber-400"}`}>
            {links.in_sync ? "The live domain matches every registered app."
              : `The live file is out of date${links.error ? ` (${links.error})` : ""} — run the sync, then redeploy.`}
          </p>
          <Button data-testid="store-assetlinks-recheck" variant="outline" size="sm"
            disabled={busy} onClick={recheck} className="mt-3">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              : <RefreshCw className="mr-2 h-4 w-4" />} Check my domain now
          </Button>
          <pre data-testid="store-assetlinks-json"
            className="mt-3 max-h-64 overflow-auto rounded-lg border border-ink-600 bg-ink-900 p-3 font-mono text-[11px] text-slate-300">
{JSON.stringify(links.expected, null, 2)}
          </pre>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button data-testid="store-assetlinks-copy" variant="outline" size="sm"
              onClick={() => { copy(JSON.stringify(links.expected, null, 2)); setNotice("Verification file copied."); }}>
              <ClipboardCopy className="mr-2 h-4 w-4" /> Copy file
            </Button>
            <Button data-testid="store-assetlinks-copy-cmd" variant="ghost" size="sm"
              onClick={() => { copy(links.sync_command); setNotice("Sync command copied."); }}>
              <ClipboardCopy className="mr-2 h-4 w-4" /> Copy sync command
            </Button>
          </div>
          <p className="mt-2 break-all font-mono text-[11px] text-slate-500">{links.sync_command}</p>
        </div>
      )}

      <ApnsPanel onNotice={setNotice} />

      {config && (
        <div data-testid="store-build-config" className="rounded-xl border border-gold/30 bg-ink-800 p-5">
          <p className="font-display text-lg text-slate-100">{config.app.app_name} — build config</p>
          <p className="mt-1 text-sm text-slate-400">
            Boots into <span className="font-mono text-[11px]">{config.start_url}</span>. Build it with:
          </p>
          <pre className="mt-3 overflow-x-auto rounded-lg border border-ink-600 bg-ink-900 p-3 font-mono text-[11px] text-slate-300">
{`cd mobile && node configure.mjs --app ${config.app.id} \\
  --api ${config.build_env.REACT_APP_BACKEND_URL} --token <your admin token>`}
          </pre>
          <pre data-testid="store-build-config-json"
            className="mt-3 max-h-80 overflow-auto rounded-lg border border-ink-600 bg-ink-900 p-3 font-mono text-[11px] text-slate-300">
{JSON.stringify({ twa_manifest: config.twa_manifest, capacitor: config.capacitor,
                  build_env: config.build_env }, null, 2)}
          </pre>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button data-testid="store-build-config-copy" variant="outline" size="sm"
              onClick={() => { copy(JSON.stringify(config, null, 2)); setNotice("Build config copied."); }}>
              <ClipboardCopy className="mr-2 h-4 w-4" /> Copy everything
            </Button>
            <Button data-testid="store-review-notes-copy" variant="ghost" size="sm"
              onClick={() => { copy(config.review_notes); setNotice("Review notes copied."); }}
              className="text-slate-400">Copy app review notes</Button>
          </div>
        </div>
      )}
    </div>
  );
};
